// GEO_EXEC_R19 · the sidebar's Maya unread badge.
//
// `/api/aicmo/unread` has existed since Maya did and the front end had never once called it, so a
// proactive message was invisible until the client happened to open the Maya pane. This store is
// the poll that fixes that.
//
// Three rules, and they are the whole file:
//  1. Poll only when R19 says so. `/api/geo-exec/r19/features` 404s while the switch is off, and a
//     404 means we never start an interval — with R19 off the sidebar behaves exactly as before.
//  2. Stop while the tab is hidden. A backgrounded tab polling every 30s is a battery bug, and the
//     badge is re-read on `visibilitychange` anyway, so nothing is missed by pausing.
//  3. Never surface an error. A failed poll leaves the previous count alone; an unread badge that
//     turns into an error banner is worse than a stale number.

import { reactive } from "vue";

import { getMayaUnread, getR19Features } from "../api/maya.js";

export const mayaUnread = reactive({ enabled: false, count: 0, pollMs: 30000 });

let timer = null;
let started = false;

async function readUnread() {
  try {
    const data = await getMayaUnread();
    mayaUnread.count = Number(data?.n || 0);
  } catch {
    // leave the last known count in place
  }
}

function stopTimer() {
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
}

function startTimer() {
  if (!mayaUnread.enabled || timer !== null || document.hidden) return;
  timer = setInterval(readUnread, mayaUnread.pollMs);
}

function onVisibilityChange() {
  if (document.hidden) {
    stopTimer();
    return;
  }
  readUnread();
  startTimer();
}

export async function startMayaUnreadPolling() {
  if (started) return mayaUnread;
  started = true;
  try {
    const features = await getR19Features();
    mayaUnread.enabled = Boolean(features?.ok);
    mayaUnread.pollMs = Number(features?.unread_poll_ms || mayaUnread.pollMs);
  } catch {
    mayaUnread.enabled = false; // 404 = R19 not deployed: behave exactly as before R19
  }
  if (!mayaUnread.enabled) return mayaUnread;
  document.addEventListener("visibilitychange", onVisibilityChange);
  await readUnread();
  startTimer();
  return mayaUnread;
}

export function stopMayaUnreadPolling() {
  stopTimer();
  document.removeEventListener("visibilitychange", onVisibilityChange);
  started = false;
}
